import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SlidingStatesEnum, IBookingCreatorSlidingStates, ISharedTableModalPayload } from './types';
import { ToastType } from 'shared-types/SharedTypes';
import { IToastDialogProps } from 'shared-types';
import {appRoutes} from "appRoot/RouteManager";
import { ToastDialogCleanProps } from 'design-v2/common/ToastDialogClean/types';

const NS = 'rootDisplayReducer';

const initialState: IBookingCreatorSlidingStates = {
  slideState: SlidingStatesEnum.SlideOutComplete,
  showToast: false,
  toastPayload: {} as IToastDialogProps,
  toastCleanPayload: {} as ToastDialogCleanProps,
  routeStatus: undefined,
  isShowingNewBlockout: false,
  showNewVenueSelector: false,
  showEditCustomer: false,
  isReadyForViewActions: false,
  showBlockoutByService: false,
  showBlockoutBySectionOnFp: false,
  showBlockout: false,
  showBlockoutOnFp: false,
  showBlockoutTimePicker: false,
  showSharedTableModal: false,
  sharedTableModalPayload: {} as ISharedTableModalPayload,
  showBookingConflictsModal: false,
  conflictTableId: '',
  refreshHiddenService: false,
};

const rootDisplaySlice = createSlice({
  name: 'rootDisplay', // used in action types
  initialState,
  reducers: {
    setSlideState: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<SlidingStatesEnum>
    ) => {
      state.slideState = action.payload;
    },
    setRouteStatus: (state: IBookingCreatorSlidingStates, action: PayloadAction<string>) => {
      // @todo: add a check here to throw warning if route is not in appRoutes enum
      state.routeStatus = action.payload as appRoutes;
    },
    setRefreshHiddenService: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.refreshHiddenService = action.payload;
    },
    setSlideModeState: (state: IBookingCreatorSlidingStates, action: PayloadAction<string>) => {
      state.mode = action.payload;
    },
    setShowSharedTableModal: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showSharedTableModal = action.payload;
    },
    setSharedTableModalPayload: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<ISharedTableModalPayload>
    ) => {
      state.sharedTableModalPayload = action.payload;
    },
    setShowBookingConflictsModal: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showBookingConflictsModal = action.payload;
    },
    setConflictTableId: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<string>
    ) => {
      state.conflictTableId = action.payload;
    },
    setShowEditCustomer: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showEditCustomer = action.payload;
    },
    setShowNewVenueSelector: (state: IBookingCreatorSlidingStates, action: PayloadAction<boolean>) => {
      state.showNewVenueSelector = action.payload;
    },
    clearToast: (state: IBookingCreatorSlidingStates) => {
      state.toastPayload = {} as IToastDialogProps;
      state.toastCleanPayload = {} as ToastDialogCleanProps;
      state.showToast = false;
    },
    showToast: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<{
        msg: string;
        type: ToastType;
        hideDismissBtn: boolean;
        isImportant: boolean;
      }>
    ) => {
      state.showToast = true;
      state.toastPayload = {
        msg: action.payload.msg,
        type: action.payload.type,
        hideDismissBtn: action.payload.hideDismissBtn,
        isImportant: action.payload.isImportant,
      };
    },
    showToastFullOptions: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<IToastDialogProps>
    ) => {
      state.showToast = true;
      state.toastPayload = action.payload;
    },
    // add more property if you need to this type of action
    showNewToast: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<IToastDialogProps>
    ) => {
      state.showToast = true;
      state.toastPayload = {
        msg: action.payload.msg,
        type: action.payload.type,
        subText: action.payload.subText,
        isCleanStyle: true,
        isImportant: false
      };
    },
    showToastClean: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<ToastDialogCleanProps>
    ) => {
      state.showToast = true;
      state.toastCleanPayload = action.payload;
    },
    setIsShowingNewBlockout: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.isShowingNewBlockout = action.payload;
    },
    setIsReadyForViewActions: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.isReadyForViewActions = action.payload;
    },
    setShowBlockoutByService: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showBlockoutByService = action.payload;
    },
    setShowBlockoutBySectionOnFp: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showBlockoutBySectionOnFp = action.payload;
    },
    setShowBlockout: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showBlockout = action.payload;
    },
    setShowBlockoutOnFp: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showBlockoutOnFp = action.payload;
    },
    setShowBlockoutTimePicker: (
      state: IBookingCreatorSlidingStates,
      action: PayloadAction<boolean>
    ) => {
      state.showBlockoutTimePicker = action.payload;
    },
    // handleShowToast: (
    //   state: IBookingCreatorSlidingStates,
    //   action: (msg: string, type: ToastType, hideDismissBtn: boolean, isImportant: boolean) => void
    // ) => {
    //
    // },
    // handleShowToastFullOptions: (
    //   state: IBookingCreatorSlidingStates,
    //   action: (props: IToastActionPayload) => void
    // ) => {
    //
    // },
    // handleClearToast: (
    //   state: IBookingCreatorSlidingStates,
    //   action: () => void
    // ) => {
    //
    // }
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = rootDisplaySlice;
// Extract and export each action creator by name
export { actions as rootDisplayActions };
// Export the reducer, either as a default or named export
export default reducer;
